import React from 'react';
import { Badge, Button } from '@shared/components';
import { Trash2 } from 'lucide-react';
import type { ConfiguredAddOn } from '../../types/addon.types';
import { formatCurrency } from '@/app/utils/formatCurrency';
import { formatIsoDateOnlyShort } from '@/app/utils/formatDate';
import { toDateKey } from '@features/quote/utils/dateKey';
import { isLinkedServiceSelectable, isIndependentLink } from '../../utils/linkedAddOns';
import { isPricedLine } from '../../utils/pricedLine';

interface AddOnItemProps {
    addOn: ConfiguredAddOn;
    onRemove: (instanceId: string | number) => void;
    serviceList: { instanceId: string | number; name: string }[];
    currency?: string;
    category?: string;
    /** Only the orphan list (no card header above it) names its own instances. */
    showName?: boolean;
    /** The orphan list has no catalogue entry behind it, so its rows stay removable. */
    forceRemovable?: boolean;
    /** Position among the sibling rows of the same add-on, used to keep remove names unique. */
    instanceIndex?: number;
    instanceCount?: number;
}

const AddOnItem: React.FC<AddOnItemProps> = React.memo(
    ({
        addOn,
        onRemove,
        serviceList,
        currency = 'GBP',
        category,
        showName = false,
        forceRemovable = false,
        instanceIndex = 0,
        instanceCount = 1,
    }) => {
        const getLinkedServiceName = () => {
            if (isIndependentLink(addOn.linkedServiceId)) {
                if (!addOn.startDate) {
                    return 'Scheduled independently at a later date';
                }
                const startDateKey = toDateKey(addOn.startDate);
                return startDateKey
                    ? `Scheduled independently on ${formatIsoDateOnlyShort(startDateKey)}`
                    : 'Scheduled independently on an unavailable date';
            }
            const serviceIndex = serviceList.findIndex(
                (s) => s.instanceId === addOn.linkedServiceId
            );
            if (serviceIndex !== -1) {
                return `With Service #${serviceIndex + 1} (${serviceList[serviceIndex].name})`;
            }
            return 'With Service'; // Fallback
        };

        const scheduleName = getLinkedServiceName();
        const removeName =
            instanceCount > 1
                ? `Remove ${addOn.name} (${scheduleName}), instance ${instanceIndex + 1} of ${instanceCount}`
                : `Remove ${addOn.name} (${scheduleName})`;
        const priced = isPricedLine(addOn);

        // A line whose linked service is gone must stay removable, mandatory or not.
        const required =
            !forceRemovable &&
            addOn.mandatory &&
            isLinkedServiceSelectable(addOn.linkedServiceId ?? '', serviceList);

        return (
            <div className="flex items-center justify-between bg-mp-background p-3 rounded-lg mb-2 transition-shadow hover:shadow-sm">
                <div className="flex-1">
                    <div className="flex items-center space-x-2">
                        {showName && (
                            <span className="font-semibold text-mp-foreground">{addOn.name}</span>
                        )}
                        {category && (
                            <Badge variant="info">
                                <span className="capitalize">{category}</span>
                            </Badge>
                        )}
                        {required && (
                            <Badge variant="warning">Required</Badge>
                        )}
                    </div>
                    <div className="text-sm text-mp-muted mt-1">{scheduleName}</div>
                </div>
                <div className="flex items-center space-x-4">
                    <div className="text-right">
                        {priced ? (
                            <div className="font-semibold text-mp-foreground">
                                <span>{formatCurrency(addOn.gross_price, currency)}</span>
                                <span className="ml-1 text-xs font-normal text-mp-muted">inc VAT</span>
                            </div>
                        ) : (
                            <div className="text-sm text-mp-muted">Price unavailable</div>
                        )}
                    </div>
                    {!required && (
                        <Button
                            variant="clear"
                            size="xs"
                            onClick={() => onRemove(addOn.instanceId)}
                            startIcon={<Trash2 size={18} className="text-mp-error" />}
                            ariaLabel={removeName}
                        />
                    )}
                </div>
            </div>
        );
    }
);
export default AddOnItem;
